/** DSR erasure confirmation for a Participant — Phase 2B-dsr-3-ui.
 *
 * Parallel to `DsrEraseModal` (User subjects) but adapted to the
 * Participant shape:
 *   - Participants don't have email; confirmation uses display_name
 *     when present, otherwise the participant's UUID prefix.
 *   - Erase clears display_name + ip_hash + ua_jsonb + device_jsonb
 *     on the Participant + (when linked) the redeemed_ip_hash +
 *     redeemed_ua_hash on the Invite.
 *   - Audit/annotation/evidence/chat backrefs are preserved under
 *     Art. 17(3)(e). */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { eraseParticipant } from "./api";
import type {
  DsrParticipantEraseSummary,
  SessionParticipant,
} from "./types";


type Props = {
  participant: SessionParticipant;
  onClose: () => void;
};


function confirmTokenFor(p: SessionParticipant): string {
  if (p.display_name) return p.display_name;
  return p.id.slice(0, 8);
}


export function DsrParticipantEraseModal({ participant, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const [result, setResult] = useState<DsrParticipantEraseSummary | null>(null);

  const erase = useMutation({
    mutationFn: () => eraseParticipant(participant.id),
    onSuccess: (summary) => {
      setResult(summary);
      void qc.invalidateQueries({
        queryKey: ["session-participants", participant.session],
      });
    },
  });

  const expected = confirmTokenFor(participant);
  const confirmed = typed.trim() === expected;

  if (result) {
    const preservedFields = Object.entries(result.preserved)
      .filter(([, v]) => v)
      .map(([k]) => k.replace(/_/g, " "))
      .join(", ");
    const redactedList =
      result.redacted_fields.join(", ") ||
      t("ui.dsrParticipantEraseModal.nothingToRedact");
    return (
      <ModalShell onClose={onClose} title={t("ui.dsrParticipantEraseModal.completeTitle")}>
        <p className="text-sm text-nb-text">
          {result.already_erased
            ? t("ui.dsrParticipantEraseModal.alreadyErased")
            : t("ui.dsrParticipantEraseModal.redacted", { fields: redactedList })}
        </p>
        <p className="text-xs text-nb-text-3">
          {t("ui.dsrParticipantEraseModal.preserved", {
            basis: result.preserved_legal_basis,
            fields: preservedFields,
          })}
        </p>
        <div className="flex justify-end pt-2">
          <button type="button" className="btn-primary text-sm" onClick={onClose}>
            {t("ui.dsrParticipantEraseModal.close")}
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose} title={t("ui.dsrParticipantEraseModal.eraseTitle")}>
      <div className="space-y-2 text-sm text-nb-text">
        <p>
          {t("ui.dsrParticipantEraseModal.tombstoneBody")}
        </p>
        <p className="text-xs text-nb-text-2">
          <Trans i18nKey="ui.dsrParticipantEraseModal.preservedNote">
            Audit events, annotations, evidence, and chat messages
            authored by this participant are <strong>preserved</strong>{" "}
            under GDPR Art. 17(3)(e) (eIDAS evidence-grade carve-out).
            The Participant row itself stays — only PII columns are
            redacted. This action is{" "}
            <strong className="text-cvp-warn">irreversible</strong>.
          </Trans>
        </p>
      </div>
      <label className="block space-y-1 text-sm">
        <span className="text-nb-text-2">
          <Trans i18nKey="ui.dsrParticipantEraseModal.confirmPrompt" values={{ token: expected }}>
            Type{" "}
            <code className="text-nb-mint">{"{{token}}"}</code>{" "}
            to confirm:
          </Trans>
        </span>
        <input
          className="input w-full"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={expected}
          aria-label={t("ui.dsrParticipantEraseModal.confirmIdentifierAria")}
        />
      </label>
      {erase.isError && (
        <p className="text-xs text-cvp-danger">
          {(erase.error as Error).message ?? t("ui.dsrParticipantEraseModal.erasureFailed")}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn-ghost text-sm" onClick={onClose}>
          {t("ui.dsrParticipantEraseModal.cancel")}
        </button>
        <button
          type="button"
          className="btn-danger text-sm"
          onClick={() => erase.mutate()}
          disabled={!confirmed || erase.isPending}
        >
          {erase.isPending ? t("ui.dsrParticipantEraseModal.erasing") : t("ui.dsrParticipantEraseModal.erase")}
        </button>
      </div>
    </ModalShell>
  );
}


function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="modal-backdrop bg-black/60"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md space-y-3 rounded-lg border border-nb-border bg-nb-bg p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-nb-text">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-nb-text-3 hover:text-nb-text"
            aria-label={t("ui.dsrParticipantEraseModal.closeAria")}
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}
